/**
 * NavigatorX Extended Audit & Security Service
 * 
 * Comprehensive audit logging and security monitoring:
 * - Admin action audit logs
 * - User activity tracking
 * - Security alerts
 * - GDPR compliance (data export, deletion requests)
 * - Login attempt tracking
 * - API access logs
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { navDb } from '../firebase/client';
import { NAV_USERS_COLLECTION } from './navigatorXService';

// ============================================================================
// COLLECTION CONSTANTS
// ============================================================================

const AUDIT_LOGS_COLLECTION = 'nav_audit_logs';
const ACTIVITY_LOGS_COLLECTION = 'nav_activity_logs';
const SECURITY_ALERTS_COLLECTION = 'nav_security_alerts';
const EXPORT_REQUESTS_COLLECTION = 'nav_export_requests';
const DELETION_REQUESTS_COLLECTION = 'nav_deletion_requests';
const LOGIN_ATTEMPTS_COLLECTION = 'nav_login_attempts';
const API_ACCESS_LOGS_COLLECTION = 'nav_api_logs';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface AdminAuditLog {
  id?: string;
  adminId: string;
  action: string;
  targetId?: string;
  targetType?: string;
  details?: Record<string, any>;
  timestamp: Timestamp;
  ipAddress?: string;
  userAgent?: string;
}

export interface UserActivityLog {
  id?: string;
  userId: string;
  action: string;
  details?: Record<string, any>;
  timestamp: Timestamp;
  ipAddress?: string;
  sessionId?: string;
}

export interface SecurityAlert {
  id?: string;
  type: 'suspicious_login' | 'failed_login_spike' | 'admin_action' | 'rate_limit' | 'anomaly';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  userId?: string;
  adminId?: string;
  details?: Record<string, any>;
  timestamp: Timestamp;
  acknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: Timestamp;
}

export interface DataExportRequest {
  id?: string;
  userId: string;
  userEmail?: string;
  requestedAt: Timestamp;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  completedAt?: Timestamp;
  downloadUrl?: string;
  expiresAt?: Timestamp;
  processedBy?: string;
}

export interface DeletionRequest {
  id?: string;
  userId: string;
  userEmail?: string;
  reason?: string;
  requestedAt: Timestamp;
  status: 'pending' | 'processing' | 'completed' | 'rejected';
  completedAt?: Timestamp;
  processedBy?: string;
  notes?: string;
}

export interface LoginAttempt {
  id?: string;
  userId?: string;
  email?: string;
  method: 'email' | 'google' | 'apple' | 'anonymous';
  success: boolean;
  failureReason?: string;
  ipAddress: string;
  userAgent?: string;
  location?: string;
  timestamp: Timestamp;
}

export interface ApiAccessLog {
  id?: string;
  userId?: string;
  endpoint: string;
  method: string;
  statusCode: number;
  responseTime: number;
  ipAddress: string;
  timestamp: Timestamp;
}

// ============================================================================
// ADMIN AUDIT LOGS
// ============================================================================

export async function logAdminAction(
  adminId: string,
  action: string,
  targetId?: string,
  details?: Record<string, any>
): Promise<string> {
  const docRef = await addDoc(collection(navDb, AUDIT_LOGS_COLLECTION), {
    adminId,
    action,
    targetId: targetId || null,
    details: details || null,
    timestamp: Timestamp.now()
  });
  return docRef.id;
}

export async function getAdminAuditLogs(options?: {
  adminId?: string;
  action?: string;
  limitCount?: number;
  startDate?: Date;
}): Promise<AdminAuditLog[]> {
  let q = query(
    collection(navDb, AUDIT_LOGS_COLLECTION),
    orderBy('timestamp', 'desc'),
    limit(options?.limitCount || 200)
  );
  
  const snapshot = await getDocs(q);
  let logs = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as AdminAuditLog[];
  
  // Apply filters in memory
  if (options?.adminId) {
    logs = logs.filter(l => l.adminId === options.adminId);
  }
  if (options?.action) {
    logs = logs.filter(l => l.action.includes(options.action));
  }
  if (options?.startDate) {
    logs = logs.filter(l => l.timestamp.toDate() >= options.startDate!);
  }
  
  return logs;
}

// ============================================================================
// USER ACTIVITY LOGS
// ============================================================================

export async function logUserActivity(
  userId: string,
  action: string,
  details?: Record<string, any>
): Promise<string> {
  const docRef = await addDoc(collection(navDb, ACTIVITY_LOGS_COLLECTION), {
    userId,
    action,
    details: details || null,
    timestamp: Timestamp.now()
  });
  return docRef.id;
}

export async function getUserActivityLogs(options?: {
  userId?: string;
  action?: string;
  limitCount?: number;
  startDate?: Date;
}): Promise<UserActivityLog[]> {
  let q = query(
    collection(navDb, ACTIVITY_LOGS_COLLECTION),
    orderBy('timestamp', 'desc'),
    limit(options?.limitCount || 200)
  );
  
  const snapshot = await getDocs(q);
  let logs = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as UserActivityLog[];
  
  if (options?.userId) {
    logs = logs.filter(l => l.userId === options.userId);
  }
  if (options?.action) {
    logs = logs.filter(l => l.action.includes(options.action));
  }
  if (options?.startDate) {
    logs = logs.filter(l => l.timestamp.toDate() >= options.startDate!);
  }
  
  return logs;
}

// ============================================================================
// SECURITY ALERTS
// ============================================================================

export async function createSecurityAlert(
  alert: Omit<SecurityAlert, 'id' | 'timestamp' | 'acknowledged'>
): Promise<string> {
  const docRef = await addDoc(collection(navDb, SECURITY_ALERTS_COLLECTION), {
    ...alert,
    timestamp: Timestamp.now(),
    acknowledged: false
  });
  return docRef.id;
}

export async function getSecurityAlerts(options?: {
  acknowledged?: boolean;
  severity?: SecurityAlert['severity'];
  type?: SecurityAlert['type'];
  limitCount?: number;
}): Promise<SecurityAlert[]> {
  const q = query(
    collection(navDb, SECURITY_ALERTS_COLLECTION),
    orderBy('timestamp', 'desc'),
    limit(options?.limitCount || 100)
  );
  
  const snapshot = await getDocs(q);
  let alerts = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as SecurityAlert[];
  
  if (options?.acknowledged !== undefined) {
    alerts = alerts.filter(a => a.acknowledged === options.acknowledged);
  }
  if (options?.severity) {
    alerts = alerts.filter(a => a.severity === options.severity);
  }
  if (options?.type) {
    alerts = alerts.filter(a => a.type === options.type);
  }
  
  return alerts;
}

export async function acknowledgeAlert(alertId: string, adminId: string): Promise<void> {
  await updateDoc(doc(navDb, SECURITY_ALERTS_COLLECTION, alertId), {
    acknowledged: true,
    acknowledgedBy: adminId,
    acknowledgedAt: Timestamp.now()
  });
  
  await logAdminAction(adminId, 'ACKNOWLEDGE_ALERT', alertId);
}

// ============================================================================
// DATA EXPORT REQUESTS (GDPR)
// ============================================================================

export async function createDataExportRequest(
  userId: string,
  userEmail?: string
): Promise<string> {
  const docRef = await addDoc(collection(navDb, EXPORT_REQUESTS_COLLECTION), {
    userId,
    userEmail: userEmail || null,
    requestedAt: Timestamp.now(),
    status: 'pending'
  });
  return docRef.id;
}

export async function getDataExportRequests(status?: DataExportRequest['status']): Promise<DataExportRequest[]> {
  const q = query(
    collection(navDb, EXPORT_REQUESTS_COLLECTION),
    orderBy('requestedAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  let requests = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as DataExportRequest[];
  
  if (status) {
    requests = requests.filter(r => r.status === status);
  }
  
  return requests;
}

export async function processDataExport(
  requestId: string,
  adminId: string,
  downloadUrl?: string
): Promise<void> {
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 7); // 7 day download window
  
  await updateDoc(doc(navDb, EXPORT_REQUESTS_COLLECTION, requestId), {
    status: 'completed',
    completedAt: Timestamp.now(),
    processedBy: adminId,
    downloadUrl: downloadUrl || null,
    expiresAt: Timestamp.fromDate(expiresAt)
  });
  
  await logAdminAction(adminId, 'PROCESS_DATA_EXPORT', requestId);
}

// ============================================================================
// DELETION REQUESTS (GDPR)
// ============================================================================

export async function createDeletionRequest(
  userId: string,
  reason?: string,
  userEmail?: string
): Promise<string> {
  const docRef = await addDoc(collection(navDb, DELETION_REQUESTS_COLLECTION), {
    userId,
    userEmail: userEmail || null,
    reason: reason || null,
    requestedAt: Timestamp.now(),
    status: 'pending'
  });
  return docRef.id;
}

export async function getDeletionRequests(status?: DeletionRequest['status']): Promise<DeletionRequest[]> {
  const q = query(
    collection(navDb, DELETION_REQUESTS_COLLECTION),
    orderBy('requestedAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  let requests = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as DeletionRequest[];
  
  if (status) {
    requests = requests.filter(r => r.status === status);
  }
  
  return requests;
}

export async function processDeletionRequest(
  requestId: string,
  approve: boolean,
  adminId: string,
  notes?: string
): Promise<void> {
  const request = await getDoc(doc(navDb, DELETION_REQUESTS_COLLECTION, requestId));
  if (!request.exists()) throw new Error('Request not found');
  
  const requestData = request.data() as DeletionRequest;
  
  if (approve) {
    // Mark as processing
    await updateDoc(doc(navDb, DELETION_REQUESTS_COLLECTION, requestId), {
      status: 'processing'
    });
    
    // Delete user data
    try {
      await deleteDoc(doc(navDb, NAV_USERS_COLLECTION, requestData.userId));
      
      await updateDoc(doc(navDb, DELETION_REQUESTS_COLLECTION, requestId), {
        status: 'completed',
        completedAt: Timestamp.now(),
        processedBy: adminId,
        notes: notes || null
      });
    } catch (error) {
      await updateDoc(doc(navDb, DELETION_REQUESTS_COLLECTION, requestId), {
        status: 'pending',
        notes: `Error: ${error}`
      });
      throw error;
    }
  } else {
    await updateDoc(doc(navDb, DELETION_REQUESTS_COLLECTION, requestId), {
      status: 'rejected',
      completedAt: Timestamp.now(),
      processedBy: adminId,
      notes: notes || null
    });
  }
  
  await logAdminAction(adminId, approve ? 'APPROVE_DELETION' : 'REJECT_DELETION', requestId);
}

// ============================================================================
// LOGIN ATTEMPTS
// ============================================================================

export async function logLoginAttempt(
  attempt: Omit<LoginAttempt, 'id' | 'timestamp'>
): Promise<string> {
  const docRef = await addDoc(collection(navDb, LOGIN_ATTEMPTS_COLLECTION), {
    ...attempt,
    timestamp: Timestamp.now()
  });
  
  // Check for suspicious activity
  if (!attempt.success) {
    await checkForLoginSpike(attempt.email || attempt.userId || attempt.ipAddress);
  }
  
  return docRef.id;
}

export async function getLoginAttempts(options?: {
  userId?: string;
  email?: string;
  ipAddress?: string;
  success?: boolean;
  limitCount?: number;
}): Promise<LoginAttempt[]> {
  const q = query(
    collection(navDb, LOGIN_ATTEMPTS_COLLECTION),
    orderBy('timestamp', 'desc'),
    limit(options?.limitCount || 100)
  );
  
  const snapshot = await getDocs(q);
  let attempts = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as LoginAttempt[];
  
  if (options?.userId) {
    attempts = attempts.filter(a => a.userId === options.userId);
  }
  if (options?.email) {
    attempts = attempts.filter(a => a.email === options.email);
  }
  if (options?.ipAddress) {
    attempts = attempts.filter(a => a.ipAddress === options.ipAddress);
  }
  if (options?.success !== undefined) {
    attempts = attempts.filter(a => a.success === options.success);
  }
  
  return attempts;
}

async function checkForLoginSpike(identifier: string): Promise<void> {
  const fiveMinutesAgo = new Date();
  fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);
  
  const recentAttempts = await getLoginAttempts({ limitCount: 50 });
  const failedAttempts = recentAttempts.filter(a => 
    !a.success && 
    a.timestamp.toDate() >= fiveMinutesAgo &&
    (a.email === identifier || a.userId === identifier || a.ipAddress === identifier)
  );
  
  if (failedAttempts.length >= 5) {
    await createSecurityAlert({
      type: 'failed_login_spike',
      severity: 'high',
      message: `${failedAttempts.length} failed login attempts for ${identifier} in 5 minutes`,
      details: { identifier, count: failedAttempts.length }
    });
  }
}

// ============================================================================
// API ACCESS LOGS
// ============================================================================

export async function logApiAccess(
  log: Omit<ApiAccessLog, 'id' | 'timestamp'>
): Promise<string> {
  const docRef = await addDoc(collection(navDb, API_ACCESS_LOGS_COLLECTION), {
    ...log,
    timestamp: Timestamp.now()
  });
  return docRef.id;
}

export async function getApiAccessLogs(options?: {
  userId?: string;
  endpoint?: string;
  limitCount?: number;
}): Promise<ApiAccessLog[]> {
  const q = query(
    collection(navDb, API_ACCESS_LOGS_COLLECTION),
    orderBy('timestamp', 'desc'),
    limit(options?.limitCount || 100)
  );
  
  const snapshot = await getDocs(q);
  let logs = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as ApiAccessLog[];
  
  if (options?.userId) {
    logs = logs.filter(l => l.userId === options.userId);
  }
  if (options?.endpoint) {
    logs = logs.filter(l => l.endpoint.includes(options.endpoint));
  }
  
  return logs;
}

// ============================================================================
// EXPORT FUNCTIONS
// ============================================================================

export async function exportAuditLogs(logs: AdminAuditLog[]): Promise<string> {
  const headers = ['ID', 'Admin ID', 'Action', 'Target ID', 'Timestamp', 'Details'];
  const rows = logs.map(log => [
    log.id || '',
    log.adminId,
    log.action,
    log.targetId || '',
    log.timestamp?.toDate?.()?.toISOString() || '',
    JSON.stringify(log.details || {})
  ]);
  
  return [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
}

// ============================================================================
// CONSENT LOGGING
// ============================================================================

export async function logConsent(
  userId: string,
  consentType: 'terms' | 'privacy' | 'marketing' | 'data_processing',
  granted: boolean,
  version: string
): Promise<string> {
  return await logUserActivity(userId, 'CONSENT_UPDATE', {
    consentType,
    granted,
    version,
    timestamp: new Date().toISOString()
  });
}

// ============================================================================
// CLEANUP / RETENTION
// ============================================================================

export async function cleanupOldLogs(retentionDays: number = 365): Promise<number> {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - retentionDays);
  
  let deletedCount = 0;
  
  // This would be implemented as a Cloud Function for large-scale cleanup
  // For now, just return 0 as a placeholder
  console.log(`Would clean up logs older than ${cutoffDate.toISOString()}`);

  return deletedCount;
}

// ============================================================================
// TARGET-SPECIFIC LOGS (used by UserDetailModal)
// ============================================================================

export async function getLogsForTarget(
  targetId: string,
  limitCount: number = 50
): Promise<AdminAuditLog[]> {
  const logs = await getAdminAuditLogs({ limitCount: limitCount * 2 });
  return logs.filter(l =>
    (l as any).targetId === targetId ||
    (l as any).targetUserId === targetId ||
    l.adminId === targetId
  ).slice(0, limitCount);
}
